import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import { TableHeader } from '@Features/generics/generics.type';
import { useTypedSelector } from '@Features/store';
import UserProgress from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/UserProgress';
import { Chip, TableCell, Tooltip } from '@mui/material';
import React from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { StatusChip, SubscriptionStatus } from '../Subscriptions/subscriptions.shared';
import useStudentList from '../Subscriptions/useStudentList';
import PerformanceReport from './PerformanceReport';

export type PerformanceRow = {
	type: 'standalone' | 'bundle';
	id: number;
	title: string;
	code: string;
	instructor_name: string;
	purchase_method: string | null;
	source_bundle_title: string | null;
	enrolled_at: Date;
	status: SubscriptionStatus;
	progress: number;
	items_count: number;
};

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'TYPE', fieldName: 'type', canSort: false, width: '8%' },
	{ displayName: 'TITLE', fieldName: 'title', canSort: false, width: '24%' },
	{ displayName: 'INSTRUCTOR', fieldName: 'instructor', canSort: false },
	{ displayName: 'CONTENT', fieldName: 'items_count', canSort: false },
	{ displayName: 'METHOD', fieldName: 'purchase_method', canSort: false },
	{ displayName: 'ENROLLED AT', fieldName: 'enrolled_at', canSort: false },
	{ displayName: 'STATUS', fieldName: 'status', canSort: false },
	{ displayName: 'PROGRESS', fieldName: 'progress', canSort: false },
];

/**
 * Student details → Performance: read-only. One row per live subscription
 * (standalone course or bundle); a row opens its report
 * (`:type/:subId/overview`), the Classrooms tab's counterpart.
 */
const PerformanceList: React.FC<{ studentId: number }> = ({ studentId }) => {
	const history = useHistory();
	const { url } = useRouteMatch();
	const { rows, meta, load } = useStudentList<PerformanceRow>(
		studentId,
		'performance',
		[],
		'Unable to load subscriptions'
	);

	return (
		<>
			<Title>Performance</Title>
			<MainTable
				onPageChangeRequest={load}
				tableFilters={[]}
				tableHeads={TABLE_HEADS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				renderItems={() =>
					rows.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						rows.map((row) => (
							<MainTableRow
								key={`${row.type}-${row.id}`}
								hover
								sx={{ cursor: 'pointer' }}
								onClick={() => history.push({ pathname: `${url}/${row.type}/${row.id}/overview` })}
							>
								<TableCell>
									<Chip
										size="small"
										variant="outlined"
										color={row.type === 'bundle' ? 'secondary' : 'primary'}
										label={row.type === 'bundle' ? 'Bundle' : 'Course'}
									/>
								</TableCell>
								<Tooltip title={row.code ? `${row.title} (${row.code})` : row.title} placement="top">
									<TableCell>{row.title}</TableCell>
								</Tooltip>
								<TableCell>{row.instructor_name || '-'}</TableCell>
								<TableCell>
									{row.items_count} {row.type === 'bundle' ? 'courses' : 'lectures'}
								</TableCell>
								<TableCell>
									{row.source_bundle_title ? `Bundle: ${row.source_bundle_title}` : row.purchase_method || '-'}
								</TableCell>
								<TableCell>{row.enrolled_at ? <DateTimeFormatter date={row.enrolled_at} /> : '-'}</TableCell>
								<TableCell>
									<StatusChip status={row.status} />
								</TableCell>
								<TableCell>
									<UserProgress progress={Number(row.progress || 0)} />
								</TableCell>
							</MainTableRow>
						))
					)
				}
			/>
		</>
	);
};

const Performance: React.FC = () => {
	const { path } = useRouteMatch();
	const { currentEntity } = useTypedSelector((state) => state.Students);
	const studentId: number | undefined = currentEntity?.id;
	if (!studentId) return null;
	return (
		<Switch>
			<Route exact path={path}>
				<PerformanceList studentId={studentId} />
			</Route>
			<Route path={`${path}/:type/:subId`}>
				<PerformanceReport studentId={studentId} />
			</Route>
		</Switch>
	);
};

const Title = styled.div`
	color: #251b33;
	font-size: 20px;
	font-weight: bold;
	margin-bottom: 35px;
`;

export default Performance;
